import { BuildingStorefrontIcon, GlobeAltIcon } from "@heroicons/react/24/outline";

type Flow = {
  icon: typeof GlobeAltIcon;
  eyebrow: string;
  title: string;
  intro: string;
  steps: string[];
  tint: string;
};

const FLOWS: Flow[] = [
  {
    icon: GlobeAltIcon,
    eyebrow: "Con archivo listo",
    title: "Llega para imprimir",
    intro:
      "Diseñadores, agencias y empresas cargan su pedido desde tu enlace público, sin crear una cuenta.",
    steps: [
      "Eligen el producto y ven las especificaciones técnicas",
      "Descargan la plantilla y adjuntan su archivo",
      "Siguen el estado de su pedido paso a paso, como el de un envío",
    ],
    tint: "oklch(60% 0.15 240)",
  },
  {
    icon: BuildingStorefrontIcon,
    eyebrow: "Necesita diseño",
    title: "Hay que crearlo primero",
    intro: "El cliente llega con una idea; tú tomas el pedido y te encargas del arte.",
    steps: [
      "Tomas el encargo por teléfono, WhatsApp o en persona",
      "Se suben las versiones del arte para aprobar",
      "Registras la aprobación: el cliente no toca el sistema",
    ],
    tint: "oklch(58% 0.19 300)",
  },
];

/**
 * "Trabajes como trabajes, un solo tablero" — the two entry flows (pro
 * self-service via public link, and retail via the shop as proxy) presented as
 * options, not a requirement: a shop may use one, the other, or both. Scales
 * from a multi-team shop down to a one-person operation.
 */
export function TwoFlows() {
  return (
    <section id="flujos" className="border-t border-[var(--separator)] py-20 sm:py-24">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="max-w-2xl">
          <p className="text-sm font-semibold uppercase tracking-wider text-[var(--muted)]">
            Cómo funciona
          </p>
          <h2 className="mt-3 text-balance text-3xl font-bold tracking-tight text-[var(--foreground)] sm:text-4xl">
            Trabajes como trabajes, un solo tablero.
          </h2>
          <p className="mt-4 text-lg text-[var(--muted)]">
            ¿Recibes archivos listos, haces el diseño tú, o las dos cosas? Usas el flujo que
            necesites; el otro simplemente no lo activas.
          </p>
        </div>

        <div className="mt-12 grid gap-5 lg:grid-cols-2">
          {FLOWS.map((flow) => {
            const Icon = flow.icon;
            return (
              <div
                key={flow.eyebrow}
                className="rounded-2xl border border-[var(--separator)] bg-[var(--surface)] p-7"
              >
                <div className="flex items-center gap-3">
                  <div
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl"
                    style={{ background: `color-mix(in oklch, ${flow.tint} 16%, transparent)` }}
                  >
                    <Icon className="h-6 w-6" style={{ color: flow.tint }} />
                  </div>
                  <div>
                    <p
                      className="text-xs font-semibold uppercase tracking-wider"
                      style={{ color: flow.tint }}
                    >
                      {flow.eyebrow}
                    </p>
                    <h3 className="text-xl font-semibold text-[var(--foreground)]">{flow.title}</h3>
                  </div>
                </div>

                <p className="mt-4 text-[15px] leading-relaxed text-[var(--muted)]">{flow.intro}</p>

                <ul className="mt-4 flex flex-col gap-2.5">
                  {flow.steps.map((step) => (
                    <li key={step} className="flex items-start gap-2.5">
                      <span
                        className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full"
                        style={{ background: flow.tint }}
                      />
                      <span className="text-[15px] leading-relaxed text-[var(--foreground)]">
                        {step}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        <p className="mx-auto mt-8 max-w-2xl text-center text-[15px] text-[var(--muted)]">
          Desde una imprenta con varios equipos hasta un diseñador que terceriza la impresión y se
          autogestiona: cada quien usa lo que necesita, ni más ni menos.
        </p>
      </div>
    </section>
  );
}
